import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useClerk } from "@clerk/react";
import { BarChart3, Bell, CalendarDays, ChevronRight, ClipboardList, CreditCard, FlaskConical, LayoutDashboard, LogOut, Menu, Mic, Receipt, Settings, ShieldCheck, Stethoscope, UserCog, Users, WalletCards, X } from "lucide-react";
import { Brand } from "@/components/common";
import { useLoad, useMember } from "@/lib/api";

export const NAV = [
  { label: "Dashboard", to: "/", icon: LayoutDashboard, perm: "patients:read" },
  { label: "Voice Entry", to: "/voice", icon: Mic, perm: "voice", dot: true },
  { label: "Patients", to: "/patients", icon: Users, perm: "patients:read" },
  { label: "Clinical Records", to: "/clinical", icon: ClipboardList, perm: "clinical:read" },
  { label: "Treatment Ledger", to: "/treatments", icon: Stethoscope, perm: "ledger:read" },
  { label: "Patient Payments", to: "/payments", icon: CreditCard, perm: "ledger:read" },
  { label: "Laboratory Ledger", to: "/lab", icon: FlaskConical, perm: "lab:read" },
  { label: "Expenses", to: "/expenses", icon: Receipt, perm: "expenses:read" },
  { label: "Cashbook", to: "/cashbook", icon: WalletCards, perm: "cashbook:read" },
  { label: "Appointments", to: "/appointments", icon: CalendarDays, perm: "appointments:read" },
  { label: "Reports", to: "/reports", icon: BarChart3, perm: "reports:read" },
  { label: "Team & Access", to: "/team", icon: UserCog, perm: "team" },
  { label: "Settings", to: "/settings", icon: Settings, perm: "settings:write" },
];

const slug = (s) => s.toLowerCase().replaceAll(" & ", "-").replaceAll(" ", "-");

export default function Shell({ children }) {
  const member = useMember();
  const { signOut } = useClerk();
  const [mobile, setMobile] = useState(false);
  const clinic = useLoad("/settings/clinic", null);
  const reloadClinic = clinic.reload;
  useEffect(() => { window.addEventListener("clinic-updated", reloadClinic); return () => window.removeEventListener("clinic-updated", reloadClinic); }, [reloadClinic]);
  const { pathname } = useLocation();
  const items = NAV.filter((n) => member.permissions.includes(n.perm));
  const active = [...items].reverse().find((n) => (n.to === "/" ? pathname === "/" : pathname.startsWith(n.to)))?.label || "Workspace";
  const name = member.name || member.email || "Team member";
  return (
    <div className="app-shell">
      <aside className={mobile ? "sidebar open" : "sidebar"}>
        <div className="side-top"><div className="brand-stack"><Brand /><span className="clinic-id">{clinic.data?.logo_data_url && <img className="sidebar-logo" src={clinic.data.logo_data_url} alt="" data-testid="sidebar-clinic-logo" />}<small className="clinic-chip" data-testid="sidebar-clinic-name">{member.clinic?.name}</small></span></div><button className="icon-btn mobile-close" onClick={() => setMobile(false)} data-testid="close-menu-button"><X /></button></div>
        <nav data-testid="main-navigation">
          {items.map(({ label, to, icon: Icon, dot }) => (
            <NavLink key={label} to={to} end={to === "/"} className={({ isActive }) => (isActive ? "nav-item active" : "nav-item")} onClick={() => setMobile(false)} data-testid={`nav-${slug(label)}`}>
              <Icon size={18} /><span>{label}</span>{dot && <i />}
            </NavLink>
          ))}
        </nav>
        <div className="side-bottom">
          <div className="security-note"><ShieldCheck size={17} /><span><b>Private workspace</b><small>Access follows your clinic role</small></span></div>
          <div className="profile" data-testid="profile-card"><div className="avatar">{name.slice(0, 2).toUpperCase()}</div><span><b data-testid="profile-name">{name}</b><small data-testid="profile-role">{member.role_label}</small></span></div>
          <button className="logout" onClick={() => signOut({ redirectUrl: "/" })} data-testid="logout-button"><LogOut size={15} /> Sign out</button>
        </div>
      </aside>
      {mobile && <div className="sidebar-scrim" onClick={() => setMobile(false)} data-testid="sidebar-scrim" />}
      <main className="main">
        <header className="topbar">
          <button className="icon-btn menu-trigger" onClick={() => setMobile(true)} data-testid="open-menu-button"><Menu /></button>
          <div className="crumb"><span>Workspace</span><ChevronRight size={14} /><b data-testid="breadcrumb-current">{active}</b></div>
          <div className="top-actions"><button className="icon-btn" data-testid="notifications-button"><Bell size={18} /></button><div className="date-chip" data-testid="current-date">{new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</div><button className="top-signout" onClick={() => signOut({ redirectUrl: "/" })} title="Sign out" data-testid="topbar-logout-button"><LogOut size={16} /><span>Sign out</span></button></div>
        </header>
        {children}
      </main>
    </div>
  );
}
